import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Custom Validators for HxInput Component
 *
 * @see specs/012-harmony-input/contracts/ - Validation types
 */

/**
 * Validator that checks if the input contains only alphabetical characters (a-z, A-Z).
 *
 * @example
 *   ```typescript
 *   const nameControl = new FormControl('', [alphabeticalValidator()]);
 *   ```;
 *
 * @returns A validator function that returns an error object if validation fails, or null if valid.
 */
export function alphabeticalValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Don't validate empty values
    }

    const alphabeticalPattern = /^[a-zA-Z\s]*$/;
    const isValid = alphabeticalPattern.test(control.value);

    return isValid ? null : { alphabetical: { value: control.value } };
  };
}

/**
 * Validator that checks if the input contains only numerical characters (0-9).
 *
 * @example
 *   ```typescript
 *   const phoneControl = new FormControl('', [numericalValidator()]);
 *   ```;
 *
 * @returns A validator function that returns an error object if validation fails, or null if valid.
 */
export function numericalValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Don't validate empty values
    }

    const numericalPattern = /^[0-9]*$/;
    const isValid = numericalPattern.test(control.value);

    return isValid ? null : { numerical: { value: control.value } };
  };
}

/** Options for special character validator */
export interface SpecialCharValidatorOptions {
  /** Characters to allow (whitelist mode) */
  allowedChars?: string;
  /** Characters to disallow (blacklist mode) */
  disallowedChars?: string;
  /** Validation mode: 'whitelist' (explicit allow) or 'blacklist' (explicit deny) */
  mode?: 'whitelist' | 'blacklist';
}

/** Helper function to escape special regex characters */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validator that checks if the input contains only allowed special characters or excludes disallowed ones.
 *
 * @example
 *   ```typescript
 *   // Whitelist mode (allow only specific special characters)
 *   const emailControl = new FormControl('', [specialCharacterValidator({ allowedChars: '@.-_' })]);
 *
 *   // Blacklist mode (disallow specific characters)
 *   const notesControl = new FormControl('', [specialCharacterValidator({ disallowedChars: '<>', mode: 'blacklist' })]);
 *   ```;
 *
 * @param options - Configuration for allowed/disallowed characters and validation mode
 * @returns A validator function that returns an error object if validation fails, or null if valid.
 */
export function specialCharacterValidator(options?: SpecialCharValidatorOptions): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Don't validate empty values
    }

    const mode = options?.mode || 'whitelist';
    const allowedChars = options?.allowedChars || '@#$-_.';
    const disallowedChars = options?.disallowedChars || '';

    if (mode === 'whitelist') {
      // Whitelist: Only alphanumeric + explicitly allowed special characters
      const pattern = new RegExp(`^[a-zA-Z0-9${escapeRegex(allowedChars)}]*$`);
      const isValid = pattern.test(control.value);
      return isValid ? null : { specialCharacter: { value: control.value } };
    } else {
      // Blacklist: Reject if contains any disallowed characters
      if (!disallowedChars) {
        return null; // No characters to blacklist
      }
      const pattern = new RegExp(`[${escapeRegex(disallowedChars)}]`);
      const hasDisallowed = pattern.test(control.value);
      return hasDisallowed ? { specialCharacter: { value: control.value } } : null;
    }
  };
}

/**
 * Validator that rejects empty, null, or whitespace-only values. Returns `{ required: true }` so it integrates with
 * `requiredErrorMessage` on HxInput.
 *
 * @example
 *   ```typescript
 *   const commentControl = new FormControl('', [noWhitespaceValidator]);
 *   ```;
 *
 * @returns A validation error `{ required: true }` if the value is blank or whitespace-only, otherwise null.
 */
export function noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') return { required: true };
  if (typeof value === 'string' && value.trim().length === 0) return { required: true };
  return null;
}
